/** Shared API fetcher with unified response envelope support. */

// ── Error classification ─────────────────────────────────────

export type ApiErrorKind = "auth" | "permission" | "server" | "network" | "unknown";

export class ApiError extends Error {
    kind: ApiErrorKind;
    status: number | null;

    constructor(message: string, kind: ApiErrorKind, status: number | null = null) {
        super(message);
        this.name = "ApiError";
        this.kind = kind;
        this.status = status;
    }
}

const DEFAULT_TIMEOUT_MS = 30000;
const MAX_GET_ATTEMPTS = 2;
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

/** Map an HTTP status to a user-friendly ApiError. */
function httpError(status: number, fallback?: string): ApiError {
    if (status === 401) {
        return new ApiError("Session expired — please log in again.", "auth", 401);
    }
    if (status === 403) {
        return new ApiError(
            "Permission denied — your Xero connection may have expired. Reconnect in Settings.",
            "permission",
            403,
        );
    }
    if (status >= 500) {
        return new ApiError(
            fallback || "Server error — please try again in a moment.",
            "server",
            status,
        );
    }
    return new ApiError(fallback || `Request failed (${status})`, "unknown", status);
}

function isTransientNetworkError(err: unknown): boolean {
    if (!(err instanceof Error)) return false;
    if (err.name === "AbortError") return true;
    return (
        err.message === "Failed to fetch"
        || err.message === "Load failed"
        || err.message.includes("NetworkError")
    );
}

function backoffMs(attempt: number): number {
    return 200 * Math.pow(2, Math.max(0, attempt - 1));
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, {
            method: "GET",
            headers: {"Content-Type": "application/json"},
            credentials: "include",
            signal: controller.signal,
        });
    } finally {
        clearTimeout(timer);
    }
}

/** Return a user-friendly message for any error object. */
export function friendlyErrorMessage(err: unknown): string {
    if (err instanceof ApiError) return err.message;
    if (err instanceof Error) {
        if (err.message === "Failed to fetch" || err.message === "Load failed") {
            return "Network error — check your connection and try again.";
        }
        return err.message;
    }
    return "An unexpected error occurred.";
}

/** Unwrap unified response envelope if present, otherwise return raw. */
export function unwrapEnvelope<T>(json: Record<string, unknown>): T {
    if (json.status && typeof json.status === "string" && "data" in json) {
        if (json.status === "error") {
            const errors = (json.errors as string[]) ?? [];
            throw new ApiError(errors[0] || "Request failed", "server");
        }
        return json.data as T;
    }
    return json as T;
}

/** GET fetcher with error classification and envelope unwrapping. */
export async function apiFetcher<T>(url: string): Promise<T> {
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= MAX_GET_ATTEMPTS; attempt++) {
        try {
            const response = await fetchWithTimeout(url, DEFAULT_TIMEOUT_MS);
            if (!response.ok) {
                // Try to extract backend error message from response body
                let typedError: ApiError | null = null;
                try {
                    const json = await response.json();
                    const errors = (json.errors as string[]) ?? [];
                    const msg = errors[0] || json.message || undefined;
                    typedError = httpError(response.status, msg);
                } catch {
                    typedError = httpError(response.status);
                }

                if (attempt < MAX_GET_ATTEMPTS && RETRYABLE_STATUSES.has(response.status)) {
                    await sleep(backoffMs(attempt));
                    continue;
                }
                throw typedError;
            }

            const json = await response.json();
            return unwrapEnvelope<T>(json);
        } catch (err) {
            lastError = err;
            if (attempt < MAX_GET_ATTEMPTS && isTransientNetworkError(err)) {
                await sleep(backoffMs(attempt));
                continue;
            }
            if (err instanceof ApiError) throw err;
            if (err instanceof Error && err.name === "AbortError") {
                throw new ApiError("Request timed out — please try again.", "network");
            }
            throw new ApiError("Network error — check your connection and try again.", "network");
        }
    }

    if (lastError instanceof ApiError) throw lastError;
    throw new ApiError("Network error — check your connection and try again.", "network");
}
